import React, { useCallback, useEffect, useState } from 'react'
import { LoadingSpinner } from '../../components/ui'
import { usePermission } from '../../hooks/usePermissions'
import { contractCategoriesService } from '../../services/contractCategories'
import { estoqueService } from '../../services/estoque'
import { marketingNotasService } from '../../services/marketingNotas'
import { paymentSourcesService } from '../../services/paymentSources'
import type { ContractCategory } from '../../types/contractCategories'
import type { EstoqueFornecedor, PaymentMethod } from '../../types/estoque'
import type { MarketingCampaign, MarketingEvent } from '../../types/marketingNotas'
import type { PaymentSource } from '../../types/paymentSources'
import EstoqueFornecedoresTab from '../Estoque/EstoqueFornecedoresTab'
import EstoqueFormasPagamentoTab from '../Estoque/EstoqueFormasPagamentoTab'
import { PARAMS_MODULE_NAME } from './solicitacaoUi'
import SolicitacaoStagesTab from './SolicitacaoStagesTab'
import MarketingCampanhasTab from './MarketingCampanhasTab'
import MarketingCategoriasTab from './MarketingCategoriasTab'
import MarketingEventosTab from './MarketingEventosTab'
import MarketingFontesPagadorasTab from './MarketingFontesPagadorasTab'

type GestaoSubTab =
  | 'etapas'
  | 'fornecedores'
  | 'formas_pagamento'
  | 'fontes_pagadoras'
  | 'categorias'
  | 'campanhas'
  | 'eventos'

const subTabClass = (active: boolean) =>
  `py-2.5 px-1 border-b-2 font-medium text-sm ${
    active
      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
  }`

const MarketingGestaoTab: React.FC = () => {
  const { hasPermission: canInsert } = usePermission(PARAMS_MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(PARAMS_MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(PARAMS_MODULE_NAME, 'delete')

  const [activeSubTab, setActiveSubTab] = useState<GestaoSubTab>('etapas')
  const [fornecedores, setFornecedores] = useState<EstoqueFornecedor[]>([])
  const [formasPagamento, setFormasPagamento] = useState<PaymentMethod[]>([])
  const [fontesPagadoras, setFontesPagadoras] = useState<PaymentSource[]>([])
  const [categorias, setCategorias] = useState<ContractCategory[]>([])
  const [campaigns, setCampaigns] = useState<MarketingCampaign[]>([])
  const [events, setEvents] = useState<MarketingEvent[]>([])
  const [loadingFornecedores, setLoadingFornecedores] = useState(false)
  const [loadingFormasPagamento, setLoadingFormasPagamento] = useState(false)
  const [loadingFontesPagadoras, setLoadingFontesPagadoras] = useState(false)
  const [loadingCategorias, setLoadingCategorias] = useState(false)
  const [loadingCampaigns, setLoadingCampaigns] = useState(false)
  const [loadingEvents, setLoadingEvents] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadFornecedores = useCallback(async () => {
    setLoadingFornecedores(true)
    setError(null)
    try {
      const data = await estoqueService.listFornecedores(true)
      setFornecedores(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar fornecedores.')
    } finally {
      setLoadingFornecedores(false)
    }
  }, [])

  const loadFormasPagamento = useCallback(async () => {
    setLoadingFormasPagamento(true)
    setError(null)
    try {
      const data = await estoqueService.listFormasPagamento(true)
      setFormasPagamento(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar formas de pagamento.')
    } finally {
      setLoadingFormasPagamento(false)
    }
  }, [])

  const loadFontesPagadoras = useCallback(async () => {
    setLoadingFontesPagadoras(true)
    setError(null)
    try {
      const data = await paymentSourcesService.list(true)
      setFontesPagadoras(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar fontes pagadoras.')
    } finally {
      setLoadingFontesPagadoras(false)
    }
  }, [])

  const loadCategorias = useCallback(async () => {
    setLoadingCategorias(true)
    setError(null)
    try {
      const data = await contractCategoriesService.list(true)
      setCategorias(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar categorias.')
    } finally {
      setLoadingCategorias(false)
    }
  }, [])

  const loadCampaigns = useCallback(async () => {
    setLoadingCampaigns(true)
    setError(null)
    try {
      const data = await marketingNotasService.listCampaigns(true)
      setCampaigns(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar campanhas.')
    } finally {
      setLoadingCampaigns(false)
    }
  }, [])

  const loadEvents = useCallback(async () => {
    setLoadingEvents(true)
    setError(null)
    try {
      const data = await marketingNotasService.listEvents(true)
      setEvents(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar eventos.')
    } finally {
      setLoadingEvents(false)
    }
  }, [])

  useEffect(() => {
    if (activeSubTab === 'fornecedores') void loadFornecedores()
    if (activeSubTab === 'formas_pagamento') void loadFormasPagamento()
    if (activeSubTab === 'fontes_pagadoras') void loadFontesPagadoras()
    if (activeSubTab === 'categorias') void loadCategorias()
    if (activeSubTab === 'campanhas') void loadCampaigns()
    if (activeSubTab === 'eventos') void loadEvents()
  }, [
    activeSubTab,
    loadFornecedores,
    loadFormasPagamento,
    loadFontesPagadoras,
    loadCategorias,
    loadCampaigns,
    loadEvents,
  ])

  return (
    <div>
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex flex-wrap gap-x-6 gap-y-2" aria-label="Subabas de gestão">
          <button
            type="button"
            onClick={() => setActiveSubTab('etapas')}
            className={subTabClass(activeSubTab === 'etapas')}
          >
            Etapas da Solicitação
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('fornecedores')}
            className={subTabClass(activeSubTab === 'fornecedores')}
          >
            Fornecedores
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('formas_pagamento')}
            className={subTabClass(activeSubTab === 'formas_pagamento')}
          >
            Formas de Pagamento
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('fontes_pagadoras')}
            className={subTabClass(activeSubTab === 'fontes_pagadoras')}
          >
            Fontes Pagadoras
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('categorias')}
            className={subTabClass(activeSubTab === 'categorias')}
          >
            Categorias
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('campanhas')}
            className={subTabClass(activeSubTab === 'campanhas')}
          >
            Campanhas
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('eventos')}
            className={subTabClass(activeSubTab === 'eventos')}
          >
            Eventos
          </button>
        </nav>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      {activeSubTab === 'etapas' && <SolicitacaoStagesTab />}

      {activeSubTab === 'fornecedores' && (
        <>
          {loadingFornecedores && fornecedores.length === 0 ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : (
            <EstoqueFornecedoresTab
              fornecedores={fornecedores}
              loading={loadingFornecedores}
              onChanged={loadFornecedores}
              canInsert={canInsert}
              canUpdate={canUpdate}
              canDelete={canDelete}
              moduleName={PARAMS_MODULE_NAME}
            />
          )}
        </>
      )}

      {activeSubTab === 'formas_pagamento' && (
        <>
          {loadingFormasPagamento && formasPagamento.length === 0 ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : (
            <EstoqueFormasPagamentoTab
              formasPagamento={formasPagamento}
              loading={loadingFormasPagamento}
              onChanged={loadFormasPagamento}
              canInsert={canInsert}
              canUpdate={canUpdate}
              canDelete={canDelete}
              moduleName={PARAMS_MODULE_NAME}
            />
          )}
        </>
      )}

      {activeSubTab === 'fontes_pagadoras' && (
        <>
          {loadingFontesPagadoras && fontesPagadoras.length === 0 ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : (
            <MarketingFontesPagadorasTab
              fontes={fontesPagadoras}
              loading={loadingFontesPagadoras}
              onChanged={loadFontesPagadoras}
              canInsert={canInsert}
              canUpdate={canUpdate}
              canDelete={canDelete}
              moduleName={PARAMS_MODULE_NAME}
            />
          )}
        </>
      )}

      {activeSubTab === 'categorias' && (
        <>
          {loadingCategorias && categorias.length === 0 ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : (
            <MarketingCategoriasTab
              categorias={categorias}
              loading={loadingCategorias}
              onChanged={loadCategorias}
              canInsert={canInsert}
              canUpdate={canUpdate}
              canDelete={canDelete}
              moduleName={PARAMS_MODULE_NAME}
            />
          )}
        </>
      )}

      {activeSubTab === 'campanhas' && (
        <MarketingCampanhasTab
          campaigns={campaigns}
          loading={loadingCampaigns}
          onChanged={loadCampaigns}
          canInsert={canInsert}
          canUpdate={canUpdate}
          canDelete={canDelete}
        />
      )}

      {activeSubTab === 'eventos' && (
        <MarketingEventosTab
          events={events}
          loading={loadingEvents}
          onChanged={loadEvents}
          canInsert={canInsert}
          canUpdate={canUpdate}
          canDelete={canDelete}
        />
      )}
    </div>
  )
}

export default MarketingGestaoTab
